import ReceiptIcon from '@mui/icons-material/Receipt';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';
import { CustomToolbarAction } from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';

import { useUser } from '@work4all/data';

import { ServiceContract } from '@work4all/models/lib/Classes/ServiceContract.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { canAddServiceContractsSubscription } from '@work4all/utils/lib/permissions';

import { ServiceConfirmationBillingOverlayController } from '../ServiceContractBillingOverlayController';

type Props = {
  refetch: () => Promise<void>;
  resetSelection: (defaultState?: boolean) => void;
  data: ServiceContract[];
  selectedEntities: ServiceContract[];
};

export const useServiceContractActions = (props: Props) => {
  const { refetch, resetSelection, data, selectedEntities } = props;

  const { t } = useTranslation();
  const dialogs = useDialogs();

  const user = useUser();
  const hasSubscriptionRight = canAddServiceContractsSubscription(user);

  const selectedRowsIds =
    selectedEntities?.map((serviceContract) => serviceContract?.id) ?? [];
  const selectedIds = selectedRowsIds.join(',');

  const allSelectedUnsubscribedIds = selectedRowsIds.filter((id) => {
    const item = data?.find((item) => item.id === Number(id));
    const date = DateTime.fromISO(item?.nextInvoiceDate);
    return date.isValid && date < DateTime.now();
  });

  const openBillingMask = useCallback(() => {
    dialogs.overlay(
      <ServiceConfirmationBillingOverlayController
        entity={Entities.batchBillingServiceContractsRequest}
        id={null}
        params={{
          mode: 'Add',
          selectedIds: allSelectedUnsubscribedIds.join(','),
        }}
      />,
      {
        onBeforeClose: () => {
          resetSelection();
          refetch();
        },
      }
    );
  }, [allSelectedUnsubscribedIds, dialogs, refetch, resetSelection]);

  const serviceContractActionDefinitions: CustomToolbarAction[] =
    useMemo((): CustomToolbarAction[] => {
      return selectedIds.length
        ? [
            {
              actionKey: 'Billing',
              IconComponent: ReceiptIcon,
              title: t('MASK.BILLING'),
              disabled: !hasSubscriptionRight,
              disableReason: !hasSubscriptionRight
                ? t('RIGHTS.MISSING')
                : undefined,
              handler: (id: number) => {
                if (id === undefined) return;
                openBillingMask();
              },
              sections: ['mobile', 'toolbar'],
              mode: 'Add',
              color: 'primary',
            },
          ]
        : undefined;
    }, [hasSubscriptionRight, openBillingMask, selectedIds.length, t]);

  return { left: serviceContractActionDefinitions };
};
